import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  MapPin, Eye, Calendar, Package, Truck, ShoppingBag, BadgeCheck, Building2, User,
  MessageSquare, HandCoins, Flag, Loader2, Sprout,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { FavoriteButton } from "@/components/FavoriteButton";
import { ProductCard } from "@/components/ProductCard";
import { LoadingState } from "@/components/States";
import { priceLabel, formatTRY, formatDate, timeAgo } from "@/lib/format";
import { HARVEST_MAP, REPORT_REASONS } from "@/lib/constants";
import api, { imageUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);

  useEffect(() => {
    setLoading(true);
    setActive(0);
    api.get(`/listings/${id}`).then(({ data }) => setData(data))
      .catch(() => toast.error("İlan yüklenemedi"))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [id]);

  if (loading) return <LoadingState />;
  if (!data) return <div className="py-20 text-center text-slate-500">İlan bulunamadı.</div>;

  const l = data.listing;
  const s = l.seller || {};
  const images = (l.images || []).map(imageUrl);
  const isOwner = user && user.user_id === l.seller_id;

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 lg:px-6 lg:py-8">
      <nav className="mb-4 flex items-center gap-1.5 text-sm text-slate-500">
        <Link to="/" className="hover:text-emerald-700">Ana Sayfa</Link> /
        <Link to={`/ilanlar?category=${l.category}`} className="hover:text-emerald-700">{l.category}</Link> /
        <span className="truncate text-slate-700">{l.title}</span>
      </nav>

      <div className="grid gap-8 lg:grid-cols-5">
        {/* Gallery + details */}
        <div className="lg:col-span-3">
          <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
            <div className="aspect-[4/3]">
              {images[active] ? (
                <img src={images[active]} alt={l.title} className="h-full w-full object-cover" data-testid="gallery-main" />
              ) : <div className="flex h-full items-center justify-center text-slate-300">Görsel yok</div>}
            </div>
            <div className="absolute left-3 top-3"><StatusBadge condition={l.condition} /></div>
            <div className="absolute right-3 top-3"><FavoriteButton listingId={l.listing_id} /></div>
          </div>
          {images.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar">
              {images.map((img, i) => (
                <button key={i} onClick={() => setActive(i)} data-testid={`gallery-thumb-${i}`}
                  className={`h-20 w-20 shrink-0 overflow-hidden rounded-lg border-2 ${i === active ? "border-emerald-600" : "border-transparent"}`}>
                  <img src={img} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6">
            <h2 className="font-display text-lg font-bold text-slate-900">Açıklama</h2>
            <p className="mt-3 whitespace-pre-line text-slate-700">{l.description || "Açıklama girilmemiş."}</p>

            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
              <Spec icon={Package} label="Mevcut Miktar" value={l.quantity != null ? `${l.quantity} ${l.unit}` : "—"} />
              <Spec icon={ShoppingBag} label="Min. Sipariş" value={l.min_order != null ? `${l.min_order} ${l.unit}` : "—"} />
              <Spec icon={MapPin} label="Konum" value={`${l.city}${l.district ? " / " + l.district : ""}`} />
              <Spec icon={Truck} label="Teslimat" value={(l.delivery_options || []).join(", ") || "—"} />
              <Spec icon={Calendar} label="Yayın Tarihi" value={formatDate(l.created_at)} />
              <Spec icon={Eye} label="Görüntülenme" value={l.views || 0} />
              {l.is_agriculture && l.harvest_status && (
                <Spec icon={Sprout} label="Hasat Durumu" value={HARVEST_MAP[l.harvest_status]} />
              )}
            </div>

            {l.price_type === "tiered" && l.tiers?.length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-bold text-slate-800">Kademeli Fiyatlandırma</h3>
                <div className="mt-2 overflow-hidden rounded-lg border border-slate-200">
                  {l.tiers.map((t, i) => (
                    <div key={i} className="flex justify-between border-b border-slate-100 px-4 py-2 text-sm last:border-0">
                      <span className="text-slate-600">{t.min_qty}{t.max_qty ? `–${t.max_qty}` : "+"} {l.unit}</span>
                      <span className="font-semibold text-emerald-900">{formatTRY(t.price)} / {l.unit}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sticky action card */}
        <div className="lg:col-span-2">
          <div className="lg:sticky lg:top-32 space-y-4">
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <h1 className="font-display text-xl font-extrabold leading-tight text-slate-900" data-testid="listing-title">{l.title}</h1>
              {l.is_agriculture && (
                <span className="mt-2 inline-flex w-fit items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700" data-testid="detail-producer-badge">
                  <Sprout size={13} /> Doğrudan Üreticiden
                </span>
              )}
              <p className="mt-3 text-3xl font-extrabold text-emerald-900" data-testid="listing-price">{priceLabel(l)}</p>
              <p className="mt-1 text-sm text-slate-500">{timeAgo(l.created_at)} yayınlandı</p>

              {!isOwner && (
                <div className="mt-5 space-y-2.5">
                  <ContactDialog listing={l} user={user} navigate={navigate} />
                  {(l.price_type === "offer" || l.price_type === "negotiable" || l.is_agriculture) && (
                    <OfferDialog listing={l} user={user} navigate={navigate} />
                  )}
                </div>
              )}
              {isOwner && (
                <Button onClick={() => navigate(`/panel/ilan-duzenle/${l.listing_id}`)} variant="outline" className="mt-5 w-full" data-testid="edit-own-listing">
                  İlanı Düzenle
                </Button>
              )}
            </div>

            {/* Seller card */}
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <Link to={`/satici/${l.seller_id}`} className="group flex items-center gap-3" data-testid="seller-profile-link">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
                  {s.account_type === "kurumsal" ? <Building2 /> : <User />}
                </span>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="truncate font-semibold text-slate-900 group-hover:text-emerald-700" data-testid="seller-name">{s.name}</p>
                    {s.corporate_approved && <BadgeCheck size={16} className="shrink-0 text-emerald-500" />}
                  </div>
                  <p className="text-xs text-slate-500">
                    {s.account_type === "kurumsal" ? "Kurumsal Satıcı" : "Bireysel Satıcı"}
                    {s.corporate_approved ? " • Onaylı" : ""}
                  </p>
                </div>
              </Link>
              {s.account_type === "kurumsal" && s.company?.company_description && (
                <p className="mt-3 text-sm text-slate-600">{s.company.company_description}</p>
              )}
              {s.city && <p className="mt-3 flex items-center gap-1 text-sm text-slate-500"><MapPin size={14} />{s.city}</p>}
              <p className="mt-1 text-xs text-slate-400">Üyelik: {formatDate(s.created_at)}</p>
              <Link to={`/satici/${l.seller_id}`} data-testid="seller-all-listings"
                className="mt-4 flex w-full items-center justify-center rounded-lg border border-slate-200 py-2 text-sm font-medium text-slate-700 hover:border-emerald-300 hover:text-emerald-700">
                Satıcının Tüm İlanları
              </Link>
            </div>

            {user && !isOwner && <ReportDialog listingId={l.listing_id} />}
          </div>
        </div>
      </div>

      {/* Similar */}
      {data.similar?.length > 0 && (
        <section className="mt-12">
          <h2 className="mb-5 font-display text-2xl font-extrabold tracking-tight text-slate-900">Benzer İlanlar</h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {data.similar.slice(0, 5).map((sl) => <ProductCard key={sl.listing_id} listing={sl} />)}
          </div>
        </section>
      )}
    </div>
  );
}

function Spec({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={18} className="mt-0.5 shrink-0 text-emerald-600" />
      <div className="min-w-0">
        <p className="text-xs text-slate-500">{label}</p>
        <p className="truncate text-sm font-semibold text-slate-800">{value}</p>
      </div>
    </div>
  );
}

function ContactDialog({ listing, user, navigate }) {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const send = async () => {
    if (!user) { navigate("/giris"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/conversations", { listing_id: listing.listing_id, message: msg });
      toast.success("Mesajınız gönderildi");
      setOpen(false);
      navigate(`/panel/mesajlar?c=${data.conversation_id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!user) { navigate("/giris"); return; } setOpen(o); }}>
      <DialogTrigger asChild>
        <Button className="w-full gap-2 bg-emerald-600 py-6 text-base font-semibold hover:bg-emerald-700" data-testid="contact-seller-btn">
          <MessageSquare size={18} /> Satıcıyla İletişime Geç
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Satıcıya Mesaj Gönder</DialogTitle>
          <DialogDescription>{listing.title}</DialogDescription>
        </DialogHeader>
        <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={4} data-testid="contact-message-input"
          placeholder="Merhaba, ürün hakkında bilgi almak istiyorum..." className="focus-visible:ring-emerald-500" />
        <DialogFooter>
          <Button onClick={send} disabled={loading || !msg.trim()} data-testid="contact-send-btn" className="bg-emerald-600 hover:bg-emerald-700">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Gönder
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OfferDialog({ listing, user, navigate }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [quantity, setQuantity] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const send = async () => {
    if (!user) { navigate("/giris"); return; }
    setLoading(true);
    try {
      await api.post("/offers", { listing_id: listing.listing_id, amount: parseFloat(amount), quantity: quantity ? parseFloat(quantity) : null, message });
      toast.success("Teklifiniz iletildi");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!user) { navigate("/giris"); return; } setOpen(o); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full gap-2 border-emerald-600 py-6 text-base font-semibold text-emerald-700 hover:bg-emerald-50" data-testid="make-offer-btn">
          <HandCoins size={18} /> Teklif Ver
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Teklif Ver</DialogTitle>
          <DialogDescription>{listing.title}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Teklif Tutarı (₺ / {listing.unit})</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="offer-amount-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Miktar ({listing.unit})</Label>
              <Input type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} data-testid="offer-quantity-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Mesaj (opsiyonel)</Label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} data-testid="offer-message-input" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={send} disabled={loading || !amount} data-testid="offer-send-btn" className="bg-emerald-600 hover:bg-emerald-700">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Teklifi Gönder
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReportDialog({ listingId }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [desc, setDesc] = useState("");

  const send = async () => {
    try {
      await api.post("/reports", { listing_id: listingId, reason, description: desc });
      toast.success("Şikayetiniz alındı");
      setOpen(false);
    } catch { toast.error("Gönderilemedi"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="flex w-full items-center justify-center gap-1.5 text-sm text-slate-500 hover:text-red-600" data-testid="report-btn">
          <Flag size={14} /> İlanı Şikayet Et
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>İlanı Şikayet Et</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger data-testid="report-reason"><SelectValue placeholder="Şikayet nedeni" /></SelectTrigger>
            <SelectContent>{REPORT_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
          <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Açıklama (opsiyonel)" rows={3} data-testid="report-desc" />
        </div>
        <DialogFooter>
          <Button onClick={send} disabled={!reason} variant="destructive" data-testid="report-send">Şikayet Gönder</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
